// Short, FanFest-scoped query param name. Kept narrow on purpose: long
// enough not to collide with random host-page params, short enough to
// keep deep-link URLs compact, and free of substrings ad-blockers use
// as signals (e.g. `ad`, `track`, `utm`, `ref`).
export const FANFEST_EMBED_ROUTE_QUERY_PARAM = "ff_r" as const;

// Previous param name. Still accepted on the READ side so links emitted
// by older SDK/API builds keep working. We never emit this name now.
export const FANFEST_EMBED_ROUTE_QUERY_PARAM_LEGACY = "fanfest_route" as const;

const ROUTE_BASE = "https://fanfest.invalid";

function normalizeComparableOrigin(origin: string): string | null {
  try {
    return new URL(origin).origin;
  } catch {
    return null;
  }
}

export function normalizeFanfestRoute(
  route: string,
  options: { appOrigin?: string } = {},
): string | null {
  const value = route.trim();
  if (!value) return null;

  if (value.startsWith("//")) {
    return null;
  }

  try {
    const appOrigin = options.appOrigin
      ? normalizeComparableOrigin(options.appOrigin)
      : null;
    const parsed = new URL(value, appOrigin ?? ROUTE_BASE);

    if (parsed.origin !== ROUTE_BASE) {
      if (!appOrigin || parsed.origin !== appOrigin) {
        return null;
      }
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function getFanfestRouteChannel(route: string): string | null {
  const normalized = normalizeFanfestRoute(route);
  if (!normalized) return null;

  try {
    const { pathname } = new URL(normalized, ROUTE_BASE);
    const firstSegment = pathname.split("/").filter(Boolean)[0];
    return firstSegment ? decodeURIComponent(firstSegment) : null;
  } catch {
    return null;
  }
}

export function isFanfestRouteForChannel(
  route: string,
  channelId: string,
): boolean {
  return getFanfestRouteChannel(route) === channelId;
}

// Drop a leading `/${channelId}` segment from a normalized route. The
// SDK already knows the channel it's embedding, so repeating it in the
// shareable URL is dead weight.
function stripChannelPrefix(route: string, channelId: string): string {
  const parsed = new URL(route, ROUTE_BASE);
  const segments = parsed.pathname.split("/").filter(Boolean);
  if (segments[0] !== channelId) {
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  const remainder = segments.slice(1);
  const newPath = remainder.length === 0 ? "/" : `/${remainder.join("/")}`;
  return `${newPath}${parsed.search}${parsed.hash}`;
}

// Re-prepend a leading `/${channelId}` to a route that doesn't carry
// one. Routes that already start with the channel (e.g. legacy links)
// are returned unchanged.
function ensureChannelPrefix(route: string, channelId: string): string {
  const parsed = new URL(route, ROUTE_BASE);
  const segments = parsed.pathname.split("/").filter(Boolean);
  if (segments[0] === channelId) {
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  const newPath =
    parsed.pathname === "/" ? `/${channelId}` : `/${channelId}${parsed.pathname}`;
  return `${newPath}${parsed.search}${parsed.hash}`;
}

export function buildHostEmbeddedAppUrl(
  hostHref: string,
  fanfestRoute: string,
  options: { appOrigin?: string; channelId?: string } = {},
): string {
  const route = normalizeFanfestRoute(fanfestRoute, options);
  if (!route) {
    throw new Error("Invalid FanFest route");
  }

  const minified = options.channelId
    ? stripChannelPrefix(route, options.channelId)
    : route;

  const hostUrl = new URL(hostHref);
  // Clear any legacy param so we never end up with both forms set.
  hostUrl.searchParams.delete(FANFEST_EMBED_ROUTE_QUERY_PARAM_LEGACY);
  hostUrl.searchParams.set(FANFEST_EMBED_ROUTE_QUERY_PARAM, minified);
  return hostUrl.href;
}

// Returns the host URL with both the new and legacy embedded-route
// params removed. Pure — callers are responsible for any
// `history.replaceState` side effect.
export function stripFanfestRouteFromHostHref(hostHref: string): string {
  try {
    const hostUrl = new URL(hostHref);
    const hadNew = hostUrl.searchParams.has(FANFEST_EMBED_ROUTE_QUERY_PARAM);
    const hadLegacy = hostUrl.searchParams.has(
      FANFEST_EMBED_ROUTE_QUERY_PARAM_LEGACY,
    );
    if (!hadNew && !hadLegacy) return hostHref;
    hostUrl.searchParams.delete(FANFEST_EMBED_ROUTE_QUERY_PARAM);
    hostUrl.searchParams.delete(FANFEST_EMBED_ROUTE_QUERY_PARAM_LEGACY);
    return hostUrl.href;
  } catch {
    return hostHref;
  }
}

export function getHostEmbeddedAppRoute(
  hostHref: string,
  options: { appOrigin?: string; channelId?: string } = {},
): string | null {
  try {
    const hostUrl = new URL(hostHref);
    const newForm = hostUrl.searchParams.get(FANFEST_EMBED_ROUTE_QUERY_PARAM);
    const legacyForm = newForm
      ? null
      : hostUrl.searchParams.get(FANFEST_EMBED_ROUTE_QUERY_PARAM_LEGACY);
    const raw = newForm ?? legacyForm;
    if (!raw) return null;
    const normalized = normalizeFanfestRoute(raw, options);
    if (!normalized) return null;
    // New `ff_r` values are channel-relative — re-prepend the channelId
    // so downstream consumers see an absolute FanFest path. Legacy
    // `fanfest_route` values already carry the channel prefix; leave
    // them alone so cross-channel links keep failing the
    // isFanfestRouteForChannel guard instead of being silently coerced.
    if (newForm && options.channelId) {
      return ensureChannelPrefix(normalized, options.channelId);
    }
    return normalized;
  } catch {
    return null;
  }
}
